import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { candidaciesMinimalQueryKey } from "@/hooks/queries/useGetCandidaciesMinimal";
import { candidacyInfoQueryKey } from "@/hooks/queries/useGetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import { HttpResponse } from "@/types";

export function useCreateAddRecommendedCandidacy() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      jobId,
      talentId,
    }: {
      companyId: string;
      jobId: string;
      sourceCandidacyId?: string | null;
      talentId?: number;
    }) => {
      const path = `companies/${companyId}/jobs/${jobId}/candidacies/recommend-candidate`;
      const body = { talentId };
      const res = await guacHttpClient.post<
        { id: string },
        HttpResponse<{ id: string }>
      >(path, body);
      return res.data;
    },
    onSuccess: (_data, { companyId, jobId, sourceCandidacyId }) => {
      // The candidacy is created immediately but enriched in the background, so
      // refresh every list that could display it rather than inserting by hand.
      void queryClient.invalidateQueries({ queryKey: ["other-candidacies"] });
      void queryClient.invalidateQueries({
        queryKey: candidaciesMinimalQueryKey(companyId, jobId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["candidacies/partial_counts_by_status"],
      });
      if (sourceCandidacyId) {
        void queryClient.invalidateQueries({
          queryKey: candidacyInfoQueryKey(
            currentCompanyId,
            currentJobId,
            sourceCandidacyId,
          ),
        });
      }
    },
  });
}
